import { Component, Output, EventEmitter, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ICON } from '@core/util/icon';
import { addressString } from '@core/util/address';
import { Address } from '@models/address';
import {
	CreateLawFirmComponent,
	CreateOrUpdateLawFirmModel,
} from './create/create.component';
import { MatDialog } from '@angular/material/dialog';
import { LawFirm, LawFirmEvent } from '@models/lawFirms';
import { LawFirmEmployee, LawFirmEmployeeType } from '@models/lawFirms';
import { LawFirmRepo } from '@repos/lawfirm/lawfirm.service';
import { LawFirmEmployeeRepo } from '@repos/lawfirmemployee/lawfirmemployee.service';
import { GlobalDataManagementRequestDispatch } from 'src/app/global-data-management/state/global-data-management/global-data-management.request.dispatch';
import { AlgoliaLawFirmEmployee } from '@fnModels/algolia/lawfirm';
import { TestHooks } from './law-firms.hooks';

@UntilDestroy()
@Component({
	selector: 'app-law-firms',
	templateUrl: './law-firms.component.html',
	standalone: false,
})
export class LawFirmsComponent {
	testHooks = TestHooks;
	@Output() onFocus = new EventEmitter<string>();
	@Input() label = '';
	@Input() collapsed = false;
	@Input() showClerk = true;
	@Input() fileNumber = '';
	@Input() showFileNumber = false;
	@Input() placeholder = '';
	@Input() lawFirm;
	@Output() onUpdate: EventEmitter<LawFirmEvent> =
		new EventEmitter<LawFirmEvent>();
	@Output() onUpdateFileNumber: EventEmitter<string> =
		new EventEmitter<string>();

	public lawFirmModel: CreateOrUpdateLawFirmModel;

	public ICON = ICON;
	public hoverShadow = true;

	constructor(
		private dialog: MatDialog,
		private globalStoreDispatch: GlobalDataManagementRequestDispatch,
		private lawFirmRepo: LawFirmRepo,
		private lawFirmEmployeeRepo: LawFirmEmployeeRepo,
	) {}

	//Lawfirm actions
	selectLawFirm = async (ev) => {
		// fetch law firm
		const lawfirm = await this.lawFirmRepo.Get(ev).toPromise();

		if (lawfirm) {
			this.onUpdate.emit({
				id: lawfirm.id,
				...lawfirm.data(),
				...ev, // This is needed for LEAP
			} as LawFirm);
		}
	};

	createLawFirm = (ev) => {
		// call the global state and create firm
		if (ev?.id) {
			this.globalStoreDispatch.RequestLawFirmChange(ev);
		}

		ev.createLawFirm = true; // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
		this.onUpdate.emit(ev);
	};

	updateLawFirm() {
		this.lawFirmModel = { name: this.lawFirm.name, lawFirm: this.lawFirm };
		const dialogRef = this.dialog.open(CreateLawFirmComponent, {
			width: '800px',
			height: '700px',
			data: { name: this.lawFirm.name, lawFirm: this.lawFirm },
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.globalStoreDispatch.RequestLawFirmChange(result);
				result.updateLawFirm = true;
				this.onUpdate.emit(result);
			}
		});
	}

	removeLawFirm = () => this.onUpdate.emit(null);

	async selectLawyerFromIntitialSearch(ev: AlgoliaLawFirmEmployee) {
		const lawfirm = await this.lawFirmRepo.Get(ev.lawFirmId).toPromise();
		const employee = await this.lawFirmEmployeeRepo
			.Get(ev.id, { lawFirmId: ev.lawFirmId })
			.toPromise();

		const employeeData = employee.data();
		employeeData.id = ev.id;

		this.onUpdate.emit({
			...(lawfirm.data() as LawFirm),
			id: ev.lawFirmId,
			lawyer: employeeData as LawFirmEmployee,
		});
	}

	//Lawyer actions
	selectLawyer = async (ev) => {
		const _lawFirm = structuredClone(this.lawFirm);

		// fetch lawyer
		const employee = await this.lawFirmEmployeeRepo
			.Get(ev.id, { lawFirmId: ev.lawFirmId })
			.toPromise();

		_lawFirm.leap = ev.leap;

		this.onUpdate.emit({
			..._lawFirm,
			lawyer: { id: employee.id, ...employee.data() },
			selectLawyer: true, // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
			role: LawFirmEmployeeType.LAWYER,
		});
	};

	createOrUpdateLawyer = (ev: LawFirmEmployee) => {
		this.globalStoreDispatch.RequestLawFirmEmployeeChange(this.lawFirm.id, ev);

		this.onUpdate.emit({
			...this.lawFirm,
			lawyer: ev,
			createOrUpdateLawyer: true, // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
			role: LawFirmEmployeeType.LAWYER,
		});
	};

	removeLawyer = () =>
		this.onUpdate.emit({
			...this.lawFirm,
			lawyer: null,
			removeLawyer: true, // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
		});

	//Clerk actions
	selectClerk = async (ev) => {
		// fetch clerk
		const employee = await this.lawFirmEmployeeRepo
			.Get(ev.id, { lawFirmId: ev.lawFirmId })
			.toPromise();

		this.onUpdate.emit({
			...this.lawFirm,
			clerk: { id: employee.id, ...employee.data(), leap: ev.leap },
			selectClerk: true, // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
			role: LawFirmEmployeeType.CLERK,
		});
	};

	createOrUpdateClerk = (ev) => {
		this.globalStoreDispatch.RequestLawFirmEmployeeChange(this.lawFirm.id, ev);

		this.onUpdate.emit({
			...this.lawFirm,
			clerk: ev,
			createOrUpdateClerk: true, // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
			role: LawFirmEmployeeType.CLERK,
		});
	};

	removeClerk = () => {
		this.onUpdate.emit({
			...this.lawFirm,
			clerk: null,
			removeClerk: true, // This is needed for LEAP because this component only has one event for all updates but we need to distinguish between events for LEAP
		});
	};

	lawFirmDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit':
				this.updateLawFirm();
				break;
			case 'Remove':
				this.removeLawFirm();
				break;
		}
	}

	setAddress(address: Address): string {
		return addressString(address);
	}

	fileNumberChange = (ev) => this.onUpdateFileNumber.emit(ev);
}
